import { useCallback, useEffect, useRef } from 'react';
import useApi from '../api/useApi';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from '../api/endpoints/queryParamsHelper';
import { VesselBM } from '../types/BusinessModel/Vessel/VesselBM';
import { VesselFactory } from '../factory/Vessel/VesselFactory';
import { useVesselStore } from '../context/VesselContext';

/**
 * Result type for preloaded vessel data.
 */
export interface VesselPreloadResult {
    success: boolean;
    data?: VesselBM;
    imoNumber?: number;
    error?: unknown;
}

// Module-level singleton state to track the preload across all hook instances
interface VesselPreloadState {
    promise: Promise<VesselPreloadResult> | null;
    data: VesselBM | null;
    imoNumber: number | null;
    hasStarted: boolean;
}

const preloadState: VesselPreloadState = {
    promise: null,
    data: null,
    imoNumber: null,
    hasStarted: false,
};

/**
 * Gets the IMO number from URL params or localStorage.
 * This allows us to know which vessel to preload before the vessel list is fetched.
 */
const getImoNumberFromStorageOrUrl = (): number | null => {
    // First, check URL params (higher priority)
    const urlParams = new URLSearchParams(window.location.search);
    const urlImo = urlParams.get('imoNumber');
    if (urlImo) {
        const imoNumber = parseInt(urlImo, 10);
        if (!isNaN(imoNumber) && imoNumber > 0) {
            return imoNumber;
        }
    }

    // Fall back to localStorage
    const lsImo = localStorage.getItem('selectedShipImoNumber');
    if (lsImo) {
        try {
            const imoNumber = JSON.parse(lsImo);
            if (typeof imoNumber === 'number' && imoNumber > 0) {
                return imoNumber;
            }
        } catch {
            // Invalid JSON, ignore
        }
    }

    return null;
};

/**
 * Hook for preloading vessel-specific data during app initialization.
 * This allows the GetVessel call to start in parallel with GetVessels
 * instead of waiting for the vessel list to be fetched first.
 *
 * Usage:
 * 1. Call startPreload() during app initialization (in RouterContent)
 * 2. When selectedVessel is set, check if preloaded data matches and apply it
 *
 * The preload state is shared across all hook instances via module-level state.
 */
const usePreloadVesselData = () => {
    const { apiEndpoints } = useApiEndpoints();
    const { getApiData } = useApi();
    const { setVessel, setSelectedVessel, setIsVesselDataRetrieved } = useVesselStore();

    // Store API endpoint in ref for async access
    const apiEndpointsRef = useRef(apiEndpoints);
    const getApiDataRef = useRef(getApiData);

    useEffect(() => {
        apiEndpointsRef.current = apiEndpoints;
    }, [apiEndpoints]);

    useEffect(() => {
        getApiDataRef.current = getApiData;
    }, [getApiData]);

    /**
     * Start preloading vessel data in the background.
     * This should be called during app initialization.
     * Safe to call multiple times - subsequent calls are no-ops.
     */
    const startPreload = useCallback(() => {
        // Don't start if already in progress or completed
        if (preloadState.hasStarted) {
            return;
        }

        const imoNumber = getImoNumberFromStorageOrUrl();
        if (!imoNumber) {
            // No IMO number available, nothing to preload
            return;
        }

        preloadState.hasStarted = true;
        preloadState.imoNumber = imoNumber;

        const endpoints = apiEndpointsRef.current;

        preloadState.promise = (async (): Promise<VesselPreloadResult> => {
            try {
                // Wait for endpoints to be loaded
                if (!endpoints || !endpoints['getVessel']?.url) {
                    // Endpoints not ready, retry after a short delay
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    const retryEndpoints = apiEndpointsRef.current;
                    if (!retryEndpoints || !retryEndpoints['getVessel']?.url) {
                        return { success: false, error: new Error('API endpoints not loaded') };
                    }
                }

                const endpoint = apiEndpointsRef.current['getVessel'].url;
                const queryParam = buildQueryParams({ imoNumber });
                const responseData = await getApiDataRef.current({
                    endpoint,
                    queryParam,
                    forceRefreshCache: true,
                });

                if (!responseData) {
                    return { success: false, error: new Error('Failed to preload vessel data'), imoNumber };
                }

                preloadState.data = responseData as VesselBM;
                return { success: true, data: preloadState.data, imoNumber };
            } catch (error) {
                console.error('Error preloading vessel data:', error);
                return { success: false, error, imoNumber };
            }
        })();
    }, []);

    /**
     * Apply the preloaded data if it matches the selected vessel.
     * This should be called when selectedVessel is set to avoid a duplicate API call.
     * Returns true if preloaded data was applied, false otherwise.
     */
    const applyPreloadedDataIfMatch = useCallback(
        async (selectedImoNumber: number): Promise<boolean> => {
            // Check if we have matching preloaded data
            if (!preloadState.promise || preloadState.imoNumber !== selectedImoNumber) {
                return false;
            }

            try {
                const result = await preloadState.promise;
                if (result.success && result.data && result.imoNumber === selectedImoNumber) {
                    const vesselFactory = new VesselFactory();
                    const vessel = vesselFactory.getVessel(result.data, false);
                    setVessel(vessel);
                    setSelectedVessel(vessel);
                    setIsVesselDataRetrieved(true);
                    return true;
                }
            } catch {
                // Preload failed, let normal flow handle it
            }

            return false;
        },
        [setVessel, setSelectedVessel, setIsVesselDataRetrieved]
    );

    /**
     * Get the preloaded IMO number (if any).
     */
    const getPreloadedImoNumber = useCallback((): number | null => {
        return preloadState.imoNumber;
    }, []);

    /**
     * Check if preloaded data is available for a specific IMO.
     */
    const hasPreloadedDataFor = useCallback((imoNumber: number): boolean => {
        return preloadState.data !== null && preloadState.imoNumber === imoNumber;
    }, []);

    /**
     * Reset the preload state. Useful during logout or vessel change.
     */
    const resetPreload = useCallback(() => {
        preloadState.promise = null;
        preloadState.data = null;
        preloadState.imoNumber = null;
        preloadState.hasStarted = false;
    }, []);

    return {
        startPreload,
        applyPreloadedDataIfMatch,
        getPreloadedImoNumber,
        hasPreloadedDataFor,
        resetPreload,
    };
};

export default usePreloadVesselData;
